import { useState } from 'react';
import { Phone, PhoneCall, Loader2, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

/**
 * ClickToCall Component
 * 
 * Displays a phone number with click-to-call functionality via Exotel.
 * 
 * Props:
 * - phoneNumber: The phone number to call
 * - clientCode: Optional client code for reference
 * - clientName: Optional client name for display
 * - token: Auth token for API calls
 * - showNumber: Whether to display the phone number (default: true)
 * - size: Button size ('sm', 'md', 'lg')
 */
export function ClickToCall({ 
  phoneNumber, 
  clientCode, 
  clientName, 
  token,
  showNumber = true,
  size = 'sm'
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [calling, setCalling] = useState(false);
  const [callResult, setCallResult] = useState(null);

  if (!phoneNumber) {
    return <span className="text-slate-500">-</span>;
  }

  const handleCallClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowConfirm(true);
    setCallResult(null);
  };

  const initiateCall = async () => {
    setCalling(true);
    try {
      const response = await fetch(`${API_URL}/exotel/click-to-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to_number: phoneNumber,
          client_code: clientCode,
          client_name: clientName,
          record: true,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCallResult({ success: true, message: data.message });
        toast.success('Call initiated! Check your phone.');
      } else {
        setCallResult({ success: false, message: data.detail || 'Failed to initiate call' });
        toast.error(data.detail || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Call initiation error:', error);
      setCallResult({ success: false, message: 'Network error. Please try again.' });
      toast.error('Network error. Please try again.');
    } finally {
      setCalling(false);
    }
  };

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  return (
    <>
      <button
        onClick={handleCallClick}
        className={`flex items-center gap-1.5 text-sky-400 hover:text-sky-300 transition-colors ${sizeClasses[size]}`}
        title="Click to call"
        data-testid={`click-to-call-${clientCode || phoneNumber}`}
      >
        <PhoneCall className="w-3.5 h-3.5" />
        {showNumber && <span>{phoneNumber}</span>}
      </button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Phone className="w-5 h-5 text-emerald-500" />
              Initiate Call
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {clientName ? (
                <>Call <span className="text-white font-medium">{clientName}</span> at <span className="text-emerald-400 font-mono">{phoneNumber}</span></>
              ) : (
                <>Call <span className="text-emerald-400 font-mono">{phoneNumber}</span></>
              )}
            </DialogDescription>
          </DialogHeader>

          {!callResult ? (
            <>
              <div className="py-4 text-slate-300 text-sm space-y-2">
                <p>When you click <strong>Call Now</strong>:</p>
                <ol className="list-decimal list-inside space-y-1 text-slate-400">
                  <li>You will receive a call on your registered phone</li>
                  <li>Once you answer, the client will be connected</li>
                  <li>The call will be recorded for compliance</li>
                </ol>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirm(false)}
                  disabled={calling}
                >
                  Cancel
                </Button>
                <Button
                  onClick={initiateCall}
                  disabled={calling}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {calling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <PhoneCall className="w-4 h-4 mr-2" />
                      Call Now
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className={`py-6 text-center ${callResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                {callResult.success ? (
                  <>
                    <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-lg font-medium">Call Initiated!</p>
                    <p className="text-sm text-slate-400 mt-2">Please check your phone</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-12 h-12 mx-auto mb-3" />
                    <p className="text-lg font-medium">Call Failed</p>
                    <p className="text-sm text-slate-400 mt-2">{callResult.message}</p>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirm(false);
                    setCallResult(null);
                  }}
                >
                  Close
                </Button>
                {!callResult.success && (
                  <Button
                    onClick={() => setCallResult(null)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Try Again
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClickToCall;
